import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart, Building2, BookOpen, Shield, Users,
  CheckCircle, AlertTriangle, Phone, MessageCircle,
  Award, Search, Bell, ChevronDown,
} from "lucide-react";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel, EditorialHeader, Section, Heading, EBtn } from "@/styles/editorial";

const VOLUNTEER_GUIDE = [
  { title: "Primeros pasos", icon: BookOpen, color: C.blue, steps: [
    { title: "Crear tu cuenta", content: "Regístrate con tu email o con Google. Elige el rol de 'Voluntario' para acceder a todas las funciones de ayuda." },
    { title: "Completar tu perfil", content: "Añade tu ubicación para recibir alertas de tu zona. Puedes configurar el radio de alertas según tu disponibilidad." },
    { title: "Activar notificaciones", content: "Permite las notificaciones push para recibir alertas de nuevas necesidades cerca de ti." },
  ]},
  { title: "Cómo ayudar", icon: Heart, color: C.red, steps: [
    { title: "Explorar necesidades", content: "En el Feed de Necesidades verás todas las peticiones activas. Filtra por tipo y urgencia." },
    { title: "Ofrecer ayuda", content: "Haz clic en 'Ayudar' en cualquier necesidad. Escribe cómo puedes colaborar y tu disponibilidad." },
    { title: "Coordinar con la protectora", content: "Una vez aceptada tu oferta, usa el chat integrado para coordinar los detalles." },
  ]},
  { title: "Sistema de recompensas", icon: Award, color: C.lilac, steps: [
    { title: "Gana puntos", content: "Cada acción solidaria suma puntos: ofrecer ayuda (+10), que la acepten (+25), compartir en redes (+15)..." },
    { title: "Sube de nivel", content: "Los puntos te hacen subir: Principiante → Ayudante → Colaborador → Héroe → Leyenda." },
    { title: "Colecciona badges", content: "Badges especiales por hitos: Primera Ayuda, Héroe Local, Alma Caritativa... ¡Muéstralos en tu perfil!" },
  ]},
  { title: "Red de búsqueda", icon: Search, color: C.orange, steps: [
    { title: "Animales perdidos", content: "En 'Animales Perdidos' puedes ver desaparecidos cerca. Si ves alguno, ¡reporta el avistamiento!" },
    { title: "Alertas en cadena", content: "Activa las alertas para recibir notificaciones cuando alguien aviste un animal perdido cerca." },
    { title: "Compartir carteles", content: "Descarga y comparte los carteles de 'Se Busca' en redes. Cada compartido puede marcar la diferencia." },
  ]},
];

const SHELTER_GUIDE = [
  { title: "Configurar tu protectora", icon: Building2, color: C.lilac, steps: [
    { title: "Registro de organización", content: "Crea tu cuenta como 'Protectora' e introduce los datos de tu organización." },
    { title: "Verificar ubicación", content: "Usa el buscador de municipios o geolocalización para tu ubicación exacta." },
    { title: "Personalizar perfil", content: "Añade descripción de tu misión y los animales que proteges." },
  ]},
  { title: "Gestionar emergencias", icon: AlertTriangle, color: C.red, steps: [
    { title: "Crear emergencia", content: "Selecciona el tipo: DANA, inundación, incendio, terremoto, ataque, abandono masivo..." },
    { title: "Añadir necesidades", content: "Crea necesidades específicas: material, voluntarios, transporte, acogidas, veterinario..." },
    { title: "Establecer urgencia", content: "Marca el nivel: URGENTE, PRIORIDAD, ACTIVO o INFO. Los voluntarios verán primero las más urgentes." },
  ]},
  { title: "Coordinar ayudas", icon: MessageCircle, color: C.blue, steps: [
    { title: "Revisar ofertas", content: "Cuando un voluntario ofrece ayuda, recibirás una notificación. Revisa su perfil." },
    { title: "Aceptar y coordinar", content: "Acepta ofertas y usa el chat para coordinar: fecha, hora, instrucciones..." },
    { title: "Confirmar completadas", content: "Cuando la ayuda se complete, márcala. Esto suma puntos al voluntario." },
  ]},
  { title: "Modo SOS", icon: Bell, color: C.orange, steps: [
    { title: "Cuándo usarlo", content: "El Modo SOS es para emergencias críticas que requieren respuesta masiva e inmediata." },
    { title: "Activar alerta", content: "Selecciona una emergencia y haz clic en 'MODO SOS'. Se alertará a TODOS los voluntarios." },
    { title: "Créditos SOS", content: "Tienes un número limitado de alertas SOS. Solicita más al administrador si es necesario." },
  ]},
];

const FAQ_DATA = [
  { q: "¿Es gratis usar la plataforma?", a: "Sí, Emergencia Animal: Conecta es completamente gratuita para voluntarios y protectoras. Nuestro objetivo es facilitar la ayuda sin barreras económicas." },
  { q: "¿Cómo verifican a las protectoras?", a: "Las organizaciones pasan por un proceso de verificación manual donde comprobamos que son legítimas: registro oficial, presencia en redes y referencias." },
  { q: "¿Puedo ayudar si no tengo coche?", a: "¡Por supuesto! Puedes clasificar donaciones, hacer difusión, ofrecer acogidas temporales, ayudar en eventos, tareas administrativas..." },
  { q: "¿Qué hago si encuentro un animal perdido?", a: "1) Comprueba chip en veterinario. 2) Busca carteles. 3) Mira 'Animales Perdidos'. 4) Si no encuentras al dueño, reporta el avistamiento." },
  { q: "¿Cómo funcionan las notificaciones?", a: "Push te avisan de nuevas necesidades en tu zona, mensajes de chat y alertas importantes. Configúralas en tu perfil." },
  { q: "¿Puedo ser voluntario y protectora?", a: "Cada cuenta tiene un único rol. Si gestionas una protectora pero quieres ayudar a otras, puedes crear dos cuentas." },
  { q: "¿Cómo puedo reportar un problema?", a: "Contáctanos en hola@emergenciaanimal.es o a través de redes sociales. Valoramos el feedback de la comunidad." },
];

export default function GuidesPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("volunteer");

  const guides = tab === "volunteer" ? VOLUNTEER_GUIDE : SHELTER_GUIDE;

  return (
    <div className="min-h-screen" style={{ background: C.white }} data-testid="guides-page">
      <EditorialHeader backLabel="Volver al Hub" backTo="/conecta" />

      {/* HERO */}
      <section className="px-6 md:px-12 pt-16 pb-12 md:pt-24 md:pb-16" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <EditorialLabel text="DOCUMENTACIÓN" color={C.orange} />
            <h1 className="text-[clamp(2rem,5vw,4rem)] leading-[1.05] tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.white }}>
              Guías de uso
            </h1>
            <p className="text-lg max-w-xl" style={{ fontFamily: BODY, color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>
              Todo lo que necesitas saber para sacar el máximo provecho de la plataforma.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TAB SELECTOR */}
      <div className="px-6 md:px-12 py-0" style={{ background: C.navy, borderTop: `1px solid rgba(255,255,255,0.08)` }}>
        <div className="max-w-[1400px] mx-auto flex gap-0">
          {[
            { id: "volunteer", label: "Voluntarios", icon: Heart },
            { id: "shelter", label: "Protectoras", icon: Building2 },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-all"
              style={{
                fontFamily: BODY,
                color: tab === t.id ? C.orange : "rgba(255,255,255,0.5)",
                borderBottom: tab === t.id ? `2px solid ${C.orange}` : "2px solid transparent",
                background: "transparent", border: "none", borderBottomWidth: 2,
                borderBottomStyle: "solid",
                borderBottomColor: tab === t.id ? C.orange : "transparent",
                cursor: "pointer",
              }}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* GUIDE SECTIONS */}
      <Section bg={C.white} testId="guides-content">
        <div className="max-w-[960px]">
          {guides.map((section, i) => (
            <GuideBlock key={`${tab}-${i}`} section={section} index={i} />
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section bg={C.offwhite} testId="guides-faq">
        <div className="max-w-[720px]">
          <EditorialLabel text="FAQ" color={C.navy} />
          <Heading size="md" className="mb-8">Preguntas frecuentes</Heading>

          <div className="space-y-0">
            {FAQ_DATA.map((faq, i) => (
              <FaqItem key={i} faq={faq} index={i} />
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-16" style={{ background: C.navy }}>
        <div className="max-w-[800px] mx-auto text-center">
          <Reveal>
            <Heading size="md">
              <span style={{ color: C.white }}>¿Listo para empezar?</span>
            </Heading>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <EBtn onClick={() => navigate("/auth")} bg={C.orange} color={C.navy} testId="cta-register">
                Crear cuenta
              </EBtn>
              <EBtn onClick={() => navigate("/conecta")} outline bg={C.white} testId="cta-explore">
                Explorar
              </EBtn>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

/* ─── GUIDE BLOCK ─── */
function GuideBlock({ section, index }) {
  return (
    <Reveal delay={index * 0.05}>
      <div className="mb-14 last:mb-0" data-testid={`guide-section-${index}`}>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: section.color, borderRadius: 4 }}>
            <section.icon className="w-5 h-5" style={{ color: C.white }} />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold tracking-widest" style={{ fontFamily: MONO, color: section.color }}>
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="w-8 h-px" style={{ background: section.color }} />
            <h2 className="text-xl md:text-2xl" style={{ fontFamily: DISPLAY, color: C.navy }}>{section.title}</h2>
          </div>
        </div>

        <div className="pl-14 space-y-5">
          {section.steps.map((step, j) => (
            <div key={j} className="flex items-start gap-4">
              <span className="w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                style={{ fontFamily: MONO, background: `${section.color}15`, color: section.color, borderRadius: 4 }}>
                {j + 1}
              </span>
              <div>
                <p className="text-sm font-bold mb-1" style={{ fontFamily: BODY, color: C.navy }}>{step.title}</p>
                <p className="text-sm" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.7 }}>{step.content}</p>
              </div>
            </div>
          ))}
        </div>

        {index < 3 && <div className="mt-10 h-px" style={{ background: `${C.navy}10` }} />}
      </div>
    </Reveal>
  );
}

/* ─── FAQ ITEM ─── */
function FaqItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${C.navy}10` }} data-testid={`faq-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
        style={{ background: "transparent", border: "none", cursor: "pointer" }}
      >
        <span className="text-sm md:text-base font-semibold pr-4" style={{ fontFamily: BODY, color: C.navy }}>{faq.q}</span>
        <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform" style={{ color: C.gray, transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <div className="pb-5 pr-8">
          <p className="text-sm" style={{ fontFamily: BODY, color: C.gray, lineHeight: 1.7 }}>{faq.a}</p>
        </div>
      )}
    </div>
  );
}
